import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AppShellStatus } from "@fox1ck-org/ui";

import { useSearchConversions } from "@/lib/services/conversion-service";
import { postbacksWithoutClick, useDataHealth } from "@/lib/services/data-health-service";
import { plural, useT } from "@/lib/i18n";
import { formatAge, formatInt } from "@/lib/format";
import { addDays, dayIn } from "@/lib/period";
import { carryPeriod } from "@/lib/route-tab";
import { useReportTimezone } from "@/lib/services/workspace-service";

/** Older than this and the dot turns amber: quiet traffic, or a stalled pipe. */
const STALE_SECONDS = 15 * 60;
/** vibe-fb pushes spend every 15 minutes; three missed pushes is a stall. */
const SPEND_STALE_SECONDS = 45 * 60;
const POLL_MS = 60_000;

/**
 * The status line's left part (доска Foot): are clicks being written and how
 * late the reports are, when the last postback came, how many postbacks
 * matched no click, when Facebook spend last arrived. Clicks, lag, the
 * unmatched postbacks and the spend push are GetDataHealth (the same facts
 * the alerts read); the last postback is the newest conversion status the
 * journal holds. Nothing here is counted in the browser.
 */
export function StatusLine() {
  const t = useT();
  const { search } = useLocation();
  const now = useNow(POLL_MS);
  const health = useDataHealth();
  const tz = useReportTimezone();
  const today = dayIn(new Date(now), tz);
  // Two days, so the line survives midnight with a postback from 23:59.
  const convs = useSearchConversions(
    { range: { from: addDays(today, -1), to: today }, limit: 1 },
    { refetchInterval: POLL_MS, staleTime: POLL_MS / 2 },
  );

  const h = health.data;
  const clickAge = ageOf(h?.traffic?.lastVisitAt, now);
  const lag = h?.reports?.lagSeconds;
  const convAge = ageOf(convs.data?.items[0]?.ts, now);
  const orphans = postbacksWithoutClick(h);
  const spendAge = ageOf(h?.spendSync?.lastPushedAt, now);

  return (
    <>
      {health.isLoading ? null : health.error ? (
        <AppShellStatus tone="danger" href="/data-health">
          {t("foot.unavailable")}
        </AppShellStatus>
      ) : clickAge === undefined || clickAge > STALE_SECONDS ? (
        <AppShellStatus tone="warn" href="/data-health">
          {clickAge === undefined ? t("foot.noClicks") : <Age text={t("foot.lastClick", { age: AGE })} age={clickAge} />}
        </AppShellStatus>
      ) : (
        <AppShellStatus tone="ok" href="/data-health">
          <Age text={t("foot.clicksWritten", { lag: AGE })} age={lag ?? 0} />
        </AppShellStatus>
      )}
      {convs.isLoading || convs.error ? null : (
        <AppShellStatus tone={convAge === undefined || convAge > STALE_SECONDS ? "warn" : "ok"}>
          {convAge === undefined ? t("foot.noPostbacks") : <Age text={t("foot.lastPostback", { age: AGE })} age={convAge} />}
        </AppShellStatus>
      )}
      {orphans > 0 ? (
        <AppShellStatus tone="warn" href={`/journal/postbacks${carryPeriod(search)}`}>
          {t("foot.withoutClick", { count: formatInt(orphans), postbacks: plural(t("word.postbacks"), orphans) })}
        </AppShellStatus>
      ) : null}
      {spendAge === undefined ? null : (
        <AppShellStatus tone={spendAge > SPEND_STALE_SECONDS ? "warn" : "info"} href="/library/sources">
          <Age text={t("ops.foot.spend", { age: AGE })} age={spendAge} />
        </AppShellStatus>
      )}
    </>
  );
}

/** The status line's right part: in which zone the day is drawn, in which currency the money is. */
export function StatusLineEnd() {
  const t = useT();
  const tz = useReportTimezone();
  return (
    <>
      <span>{t("foot.day", { tz })}</span>
      <span>{t("foot.currency")}</span>
    </>
  );
}

/** A placeholder the age is spliced into, so the figure can sit in a `.num` span. */
const AGE = "\u0000";

/** «Постбек 40 с назад» with the figure set in mono, as the board draws it. */
function Age({ text, age }: { text: string; age: number }) {
  const [before, after = ""] = text.split(AGE);
  return (
    <span>
      {before}
      <span className="num">{formatAge(age)}</span>
      {after}
    </span>
  );
}

/** Seconds from an RFC 3339 instant to `now`, or undefined. Pure. */
export function ageOf(ts: string | undefined, now: number): number | undefined {
  if (!ts) return undefined;
  const at = Date.parse(ts);
  return Number.isFinite(at) ? Math.max(0, (now - at) / 1000) : undefined;
}

function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(id);
  }, [everyMs]);
  return now;
}
